import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CREDIT_CURRENCIES, PAYMENT_METHODS, formatMoney, type CreditCustomer, type CreditTxType } from '@/lib/credit';
import { uploadMediaFile } from '@/hooks/useUpload';

interface Props {
  mode: CreditTxType;
  customer: CreditCustomer;
  currency: string;
  balance: number;
  onClose: () => void;
  onSubmit: (amount: number, description: string, paymentMethod?: string, proofUrl?: string | null) => Promise<void>;
}

const MODE_META: Record<CreditTxType, { icon: string; accent: string; titleKey: string; hintKey: string }> = {
  credit: { icon: 'ri-arrow-down-line', accent: 'bg-primary-500', titleKey: 'crf.tx_add_credit', hintKey: 'crf.tx_hint_credit' },
  payment: { icon: 'ri-arrow-up-line', accent: 'bg-accent-500', titleKey: 'crf.tx_record_payment', hintKey: 'crf.tx_hint_payment' },
  deposit: { icon: 'ri-wallet-3-line', accent: 'bg-accent-500', titleKey: 'crf.tx_deposit', hintKey: 'crf.tx_hint_deposit' },
  adjustment: { icon: 'ri-equalizer-line', accent: 'bg-secondary-500', titleKey: 'crf.tx_adjustment', hintKey: 'crf.tx_hint_adjustment' },
  transfer_out: { icon: 'ri-send-plane-line', accent: 'bg-secondary-500', titleKey: 'crf.tx_transfer_out', hintKey: 'crf.tx_hint_transfer_out' },
  transfer_in: { icon: 'ri-inbox-archive-line', accent: 'bg-accent-500', titleKey: 'crf.tx_transfer_in', hintKey: 'crf.tx_hint_transfer_in' },
};

export default function TransactionModal({ mode, customer, currency, balance, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(mode === 'payment' || mode === 'deposit' ? PAYMENT_METHODS[0] : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Photo de preuve (produits pris à crédit) — prise directement au téléphone.
  const [proofUrl, setProofUrl] = useState('');
  const [proofBusy, setProofBusy] = useState(false);
  const [proofError, setProofError] = useState('');
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  const handleProof = async (file: File | undefined) => {
    if (!file) return;
    setProofBusy(true);
    setProofError('');
    try {
      const url = await uploadMediaFile(file, 'credit-proofs');
      setProofUrl(url);
    } catch (err) {
      setProofError(err instanceof Error ? err.message : "La photo n'a pas pu être envoyée.");
    } finally {
      setProofBusy(false);
      if (cameraRef.current) cameraRef.current.value = '';
      if (galleryRef.current) galleryRef.current.value = '';
    }
  };

  const meta = MODE_META[mode];
  const metaTitle = t(meta.titleKey, { defaultValue: meta.titleKey });
  const metaHint = t(meta.hintKey, { defaultValue: meta.hintKey });
  const numericAmount = parseFloat(amount.replace(',', '.'));
  const valid = !Number.isNaN(numericAmount) && numericAmount !== 0 && (mode !== 'adjustment' ? numericAmount > 0 : true);
  const available = currency && !CREDIT_CURRENCIES.includes(currency) ? 'MAD' : currency;

  const quickAmounts = mode === 'payment' && balance > 0
    ? [balance, Math.round(balance / 2), 100, 50].filter((v, i, arr) => v > 0 && arr.indexOf(v) === i)
    : mode === 'deposit'
      ? [100, 200, 500, 1000]
      : [50, 100, 200, 500];

  const handleConfirm = async () => {
    if (!valid) {
      setError(t('crf.invalid_amount', { defaultValue: 'Saisissez un montant valide.' }));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSubmit(numericAmount, description.trim(), paymentMethod || undefined, proofUrl || null);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('crf.save_error', { defaultValue: 'Erreur lors de l\u2019enregistrement' }));
    } finally {
      setSaving(false);
    }
  };

  const newBalance =
    mode === 'credit' ? balance + (numericAmount || 0)
      : mode === 'payment' || mode === 'deposit' ? balance - (numericAmount || 0)
        : balance + (numericAmount || 0);

  // Calcul automatique : la dette est déduite en premier, le surplus va au solde.
  const amt = Number.isNaN(numericAmount) ? 0 : numericAmount;
  const debt = Math.max(0, balance);
  const appliedToDebt = Math.min(amt, debt);
  const remainderToStore = Math.max(0, amt - debt);
  const remainingDebt = Math.max(0, debt - amt);
  const isMoneyIn = mode === 'payment' || mode === 'deposit';

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={saving ? undefined : onClose} />
      <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
        <div className="w-full sm:max-w-md bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 max-h-[92vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 sticky top-0 bg-background-50 z-10">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className={`w-9 h-9 rounded-full ${meta.accent} text-background-50 flex items-center justify-center flex-shrink-0`}>
                <i className={`${meta.icon} text-lg`}></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground-950 truncate">{metaTitle}</h3>
                <p className="text-xs text-foreground-500 truncate">{customer.full_name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={saving}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0 disabled:opacity-40"
            >
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between text-xs bg-background-100 rounded-lg px-3 py-2.5">
              <span className="text-foreground-500">{t('crf.current_balance', { defaultValue: 'Solde actuel' })}</span>
              <span className="font-bold text-foreground-950">{formatMoney(balance, available)}</span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">{t('crf.amount', { defaultValue: 'Montant' })}</label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="decimal"
                  autoFocus
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setError(''); }}
                  placeholder="0"
                  className="w-full h-14 px-4 pr-16 rounded-xl border border-background-200/70 bg-background-50 text-2xl font-bold text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-foreground-400">
                  {available}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mt-2.5">
                {quickAmounts.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAmount(String(v))}
                    className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {formatMoney(v, available)}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-foreground-400 mt-2">{metaHint}</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">{t('crf.description', { defaultValue: 'Description' })}</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={mode === 'credit' ? t('crf.description_credit_ph', { defaultValue: 'Ex : sac de riz, huile…' }) : t('crf.description_payment_ph', { defaultValue: 'Ex : paiement partiel' })}
                className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                Photo de preuve <span className="text-foreground-400 font-normal">(optionnel)</span>
              </label>
              {proofUrl ? (
                <div className="flex items-center gap-3 p-2.5 rounded-lg border border-accent-200 bg-accent-50">
                  <img src={proofUrl} alt="Preuve" className="w-14 h-14 rounded-md object-cover object-top flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-accent-800 flex items-center gap-1">
                      <i className="ri-checkbox-circle-line"></i>Photo enregistrée
                    </p>
                    <p className="text-[11px] text-foreground-500">Elle sera liée à cette opération.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setProofUrl('')}
                    className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-500 cursor-pointer flex-shrink-0"
                    aria-label="Retirer la photo"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => cameraRef.current?.click()}
                    disabled={proofBusy}
                    className="flex-1 flex items-center justify-center gap-2 h-11 rounded-lg border border-background-200/70 bg-background-50 text-xs font-semibold text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    {proofBusy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-camera-line text-base"></i>}
                    Prendre une photo
                  </button>
                  <button
                    type="button"
                    onClick={() => galleryRef.current?.click()}
                    disabled={proofBusy}
                    className="w-11 h-11 flex items-center justify-center rounded-lg border border-background-200/70 bg-background-50 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
                    aria-label="Choisir une image"
                  >
                    <i className="ri-image-add-line"></i>
                  </button>
                  <input
                    ref={cameraRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => void handleProof(e.target.files?.[0])}
                  />
                  <input
                    ref={galleryRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => void handleProof(e.target.files?.[0])}
                  />
                </div>
              )}
              <p className="text-[11px] text-foreground-400 mt-1.5">
                {mode === 'credit'
                  ? 'Photographiez les produits remis : la preuve reste attachée au crédit.'
                  : 'Vous pouvez joindre un reçu ou une photo justificative.'}
              </p>
              {proofError && <p className="text-[11px] text-red-600 mt-1">{proofError}</p>}
            </div>

            {(mode === 'payment' || mode === 'deposit') && (
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">{t('crf.payment_method', { defaultValue: 'Moyen de paiement' })}</label>
                <div className="flex flex-wrap gap-2">
                  {PAYMENT_METHODS.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setPaymentMethod(m)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap border ${
                        paymentMethod === m
                          ? 'bg-accent-500 text-background-50 border-accent-500'
                          : 'bg-background-50 border-background-200/70 text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {valid && (
              <div className="rounded-lg border border-primary-200/60 bg-primary-50 px-3 py-2.5 space-y-1.5 text-sm">
                {mode === 'credit' ? (
                  <div className="flex items-center justify-between">
                    <span className="text-primary-700 font-medium">{t('crf.new_debt', { defaultValue: 'Nouvelle dette' })}</span>
                    <span className="font-bold text-primary-800">{formatMoney(newBalance, available)}</span>
                  </div>
                ) : isMoneyIn ? (
                  <>
                    {debt > 0 ? (
                      <div className="flex items-center justify-between">
                        <span className="text-foreground-600">{t('crf.debt_paid', { defaultValue: 'Dette payée' })}</span>
                        <span className="font-bold text-emerald-600">{formatMoney(appliedToDebt, available)}</span>
                      </div>
                    ) : (
                      <p className="text-xs text-foreground-500">{t('crf.no_debt_all_store', { defaultValue: 'Aucune dette : tout va dans son solde.' })}</p>
                    )}
                    {remainderToStore > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-foreground-600">{t('crf.goes_to_store', { defaultValue: 'Va dans son solde' })}</span>
                        <span className="font-bold text-emerald-600">{formatMoney(remainderToStore, available)}</span>
                      </div>
                    )}
                    {remainingDebt > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-foreground-600">{t('crf.still_owes', { defaultValue: 'Reste à devoir' })}</span>
                        <span className="font-bold text-red-600">{formatMoney(remainingDebt, available)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-primary-700 font-medium">{t('crf.new_balance', { defaultValue: 'Nouveau solde' })}</span>
                    <span className="font-bold text-primary-800">{formatMoney(newBalance, available)}</span>
                  </div>
                )}
              </div>
            )}

            {error && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
            )}
          </div>

          <div className="px-5 pb-5 pt-1 sticky bottom-0 bg-background-50">
            <button
              onClick={handleConfirm}
              disabled={saving || !valid}
              className={`w-full h-12 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${meta.accent} text-background-50`}
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i>{t('crf.saving', { defaultValue: 'Enregistrement...' })}</>
              ) : (
                <><i className="ri-check-line"></i>{t('crf.confirm', { defaultValue: 'Confirmer' })}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}